import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Modal, ModalBody, Button, Form, Label, Col, FormGroup, Input, Collapse, Alert} from 'reactstrap';
import _ from 'lodash';
import {sharedLabelsPath} from '../../../../reducers/common/localization';
import Select from '../../../commonComponents/customSelect/CustomSelect.container';
import {Utils} from '../../../../utils/utils';
import {ORGANIZATION_TYPES} from '../../../../constants/constants';
import {EDU_ORG} from '../../../../reducers/organization/organizations';
import './ValidateDomainModal.css';
import IconContainer from '../../../commonComponents/iconContainer/IconContainer';
import {AVAILABLE_MANAGED_OPTIONS} from '../../../../reducers/accreditation/validatedDomains';
import DomainInput from '../../../commonComponents/domainInput/DomainInput';

export const AVAILABLE_ACTIONS = {
    EDIT: 'edit',
    ADD: 'add',
    VALIDATE: 'validate'
};

const SUBMIT_LABEL_PATH_BY_ACTION = {
    [AVAILABLE_ACTIONS.EDIT]: sharedLabelsPath.save_changes_button,
    [AVAILABLE_ACTIONS.ADD]: sharedLabelsPath.add_button,
    [AVAILABLE_ACTIONS.VALIDATE]: 'accreditation_requests.validate_button'
}

const ADDRESS_FIELDS = ['orgCity', 'orgPostal', 'orgStreet'];
const CONTACT_FIELDS = ['contactName', 'contactEmail', 'contactPhone'];

class ValidateDomainModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            orgType: EDU_ORG,
            orgName: '',
            orgDomain: '',
            orgContinent: this.props.continentCode,
            orgCountry: '',
            orgCity: '',
            orgPostal: '',
            orgStreet: '',
            contactName: '',
            contactEmail: '',
            contactPhone: '',
            managedBy: AVAILABLE_MANAGED_OPTIONS.BAVA_MANAGED,
            addressSectionOpen: false,
            contactSectionOpen: false,
            fullyRegistered: null
        }
    }

    componentWillReceiveProps(nextProps) {
        if (!_.isEqual(this.props.data, nextProps.data) && !_.isNil(nextProps.data)) {
            this.setState({...nextProps.data});
            this.props.onContinentSetByCountry(nextProps.data.orgCountry);
            if (_.some(ADDRESS_FIELDS, field => !_.isEmpty(nextProps.data[field]))) {
                this.setState({addressSectionOpen: true});
            }
            if (_.some(CONTACT_FIELDS, field => !_.isEmpty(nextProps.data[field]))) {
                this.setState({contactSectionOpen: true});
            }
        }

        if (this.state.orgContinent !== nextProps.continentCode) {
            this.setState({orgContinent: nextProps.continentCode})
        }
        if (!this.props.success && nextProps.success) {
            this.cleanState();
            this.props.onCancel();
            this.props.onContinentSet('');
            if (this.props.onClose) {
                this.props.onClose();
            }
        }
    }

    render() {
        return (
            <Modal className="validate-domain-modal" isOpen={this.props.isOpen}
                   toggle={this.handleCancel}>
                <ModalBody>{this.renderModalBody()}</ModalBody>
            </Modal>
        );
    }

    renderModalBody() {
        return (
            <Form onSubmit={this.handleSubmit}>
                <div className="validate-domain-modal-label">
                    <h5>{_.get(this.props.translation, 'validated_domains.validate_domain_label')}</h5>
                </div>
                {
                    !!this.state.fullyRegistered ?
                        <Alert color="warning">{_.get(this.props.translation, 'validated_domains.fully_registered_org_hint')}</Alert>
                        : null
                }
                {this.getOrganizationFormGroup()}
                <div className="section-label-container olive-green" onClick={this.toggleSection('addressSectionOpen')}>
                    <h5 className="section-label">{_.get(this.props.translation, 'orgs.address_label')}</h5>
                    <IconContainer icon={'keyboard_arrow_' + (this.state.addressSectionOpen ? 'down' : 'right')}/>
                </div>
                <Collapse isOpen={this.state.addressSectionOpen}>
                    {this.getAddressFormGroup()}
                </Collapse>
                <div className="section-label-container olive-green" onClick={this.toggleSection('contactSectionOpen')}>
                    <h5 className="section-label">{_.get(this.props.translation, 'system_payment.contact_label')}</h5>
                    <IconContainer icon={'keyboard_arrow_' + (this.state.contactSectionOpen ? 'down' : 'right')}/>
                </div>
                <Collapse isOpen={this.state.contactSectionOpen}>
                    {this.getContactFormGroup()}
                </Collapse>
                <div className="modal-buttons-container">
                    <Button color="secondary" type='submit' onClick={this.handleCancel}>
                        {_.get(this.props.translation, sharedLabelsPath.cancel_button)}
                    </Button>
                    <Button className="olive-green-btn" type='submit'>
                        {_.get(this.props.translation, SUBMIT_LABEL_PATH_BY_ACTION[this.props.action])}
                    </Button>
                </div>
            </Form>
        );
    }

    getOrganizationFormGroup = () => {
        let paramsList = [
            {
                value: 'orgType',
                title: _.get(this.props.translation, 'orgs.org_type_label') + '*',
                type: 'select',
                content: Utils.getOptions(
                    _.map(ORGANIZATION_TYPES, value => ({
                        code: value.code,
                        name: _.get(this.props.translation, value.name)
                    }))
                ),
                placeholder: _.get(this.props.translation, 'register_org.org_type_placeholder'),
                isRequired: true,
                onChange: this.handleChangeOrgTypeSelect,
                isDisabled: !!this.state.fullyRegistered
            },
            {
                value: 'orgName',
                title: _.get(this.props.translation, 'orgs.name_label') + '*',
                isRequired: true,
                placeholder: _.get(this.props.translation, 'orgs.name_label'),
                isDisabled: !!this.state.fullyRegistered
            },
            {
                value: 'orgDomain',
                type: 'domain',
                title: _.get(this.props.translation, sharedLabelsPath.domain_label) + '*',
                isRequired: true,
                placeholder: _.get(this.props.translation, sharedLabelsPath.domain_label),
                isDisabled: !!this.state.fullyRegistered
            },
            {
                value: 'orgContinent',
                title: _.get(this.props.translation, sharedLabelsPath.continent_filter_label) + '*',
                type: 'select',
                content: Utils.getLocationOptions(
                    this.props.continents,
                    _.get(this.props.translation, sharedLabelsPath.continents)
                ),
                placeholder: _.get(this.props.translation, sharedLabelsPath.continent_input_placeholder),
                onChange: this.handleChangeContinent,
                isRequired: true,
                isDisabled: !this.props.continents || !!this.state.fullyRegistered
            },
            {
                value: 'orgCountry',
                title: _.get(this.props.translation, sharedLabelsPath.country_label) + '*',
                type: 'select',
                content: Utils.getLocationOptions(
                    this.props.countries,
                    _.get(this.props.translation, sharedLabelsPath.countries)
                ),
                placeholder: _.get(this.props.translation, sharedLabelsPath.country_input_placeholder),
                isRequired: true,
                isDisabled: !this.props.countries || !!this.state.fullyRegistered
            },
            {
                value: 'managedBy',
                title: _.get(this.props.translation, 'validated_domains.managed_by_label'),
                type: 'select',
                content: Utils.getOptions(
                    _.values(AVAILABLE_MANAGED_OPTIONS).map(value => ({
                        code: value,
                        name: _.get(this.props.translation, 'validated_domains.' + value + '_label')
                    }))
                )
            },
        ];

        return [..._.map(paramsList, this.getFormGroup)];
    }

    getAddressFormGroup = () => {
        let paramsList = [
            {
                value: 'orgCity',
                title: _.get(this.props.translation, 'orgs.city_label'),
                placeholder: _.get(this.props.translation, 'orgs.city_label'),
                isDisabled: !!this.state.fullyRegistered
            },
            {
                value: 'orgPostal',
                title: _.get(this.props.translation, 'orgs.postal_label'),
                placeholder: _.get(this.props.translation, 'orgs.postal_label'),
                isDisabled: !!this.state.fullyRegistered
            },
            {
                value: 'orgStreet',
                title: _.get(this.props.translation, 'orgs.street_label'),
                placeholder: _.get(this.props.translation, 'orgs.street_label'),
                isDisabled: !!this.state.fullyRegistered
            }
        ];

        return [..._.map(paramsList, this.getFormGroup)];
    };

    getContactFormGroup = () => {
        let paramsList = [
            {
                value: 'contactName',
                title: _.get(this.props.translation, 'validated_domains.contact_name_label'),
                placeholder: _.get(this.props.translation, 'validated_domains.contact_name_label')
            },
            {
                value: 'contactEmail',
                type: 'email',
                title: _.get(this.props.translation, 'validated_domains.contact_email_label'),
                placeholder: _.get(this.props.translation, 'validated_domains.contact_email_label')
            },
            {
                value: 'contactPhone',
                title: _.get(this.props.translation, 'validated_domains.contact_phone_label'),
                placeholder: _.get(this.props.translation, 'validated_domains.contact_phone_label')
            }
        ];

        return [..._.map(paramsList, this.getFormGroup)];
    }

    getFormGroup = (params) => {
        if (!params || typeof (params) !== 'object') {
            return;
        }

        switch (params.type) {
            case 'select':
                return this.getSelectFormGroup(params);
            case 'domain':
                return this.getOrganizationDomainFormGroup(params);
            default:
                let id = _.kebabCase(params.value);
                return (
                    <FormGroup row key={params.title}>
                        <Label for={id} sm={4}>{params.title}</Label>
                        <Col sm={8}>
                            <Input type={params.type || 'text'} id={id}
                                   value={this.state[params.value] || ''}
                                   onChange={params.onChange || this.handleChange(params.value)}
                                   placeholder={params.placeholder || params.title}
                                   disabled={params.isDisabled}
                                   required={params.isRequired}
                            >
                                {params.content}
                            </Input>
                        </Col>
                    </FormGroup>
                );
        }
    };

    getSelectFormGroup = (params) => {
        if (!params || typeof (params) !== 'object') {
            return;
        }
        let id = _.kebabCase(params.value);
        return (
            <FormGroup row key={params.title}>
                <Label for={id} sm={4}>{params.title}</Label>
                <Col sm={8}>
                    <Select id={id}
                            selectedOption={_.find(params.content, option => option.value === this.state[params.value])}
                            options={params.content}
                            onSelect={params.onChange || this.handleChangeSelect(params.value)}
                            placeholder={params.placeholder}
                            disabled={params.isDisabled}
                            requiredFieldError={this.state.requiredFieldError === params.value}
                    />
                </Col>
            </FormGroup>
        );
    };

    getOrganizationDomainFormGroup = (params) => {
        let id = _.kebabCase(params.value);
        return (
            <FormGroup row key={params.title}>
                <Label for={id} sm={4}>{params.title}</Label>
                <Col sm={8}>
                    <DomainInput id={id}
                                 value={this.state[params.value]}
                                 onChange={this.handleChangeDomainInput}
                                 placeholder={params.placeholder || params.title}
                                 disabled={params.isDisabled}
                                 required={params.isRequired}/>
                </Col>
            </FormGroup>
        );
    };

    handleChangeDomainInput = (data) => {
        this.setState({orgDomain: data});
    };

    handleChange = (fieldName) => (e) => {
        this.setState({[fieldName]: e.target.value});
    }

    handleChangeSelect = (fieldName) => (selectedOption) => {
        this.setState({[fieldName]: selectedOption.value});
    };

    handleChangeContinent = (selectedOption) => {
        this.props.onContinentSet(selectedOption.value);
        this.setState({orgCountry: ''});
    };

    toggleSection = (sectionKey) => () => {
        this.setState({[sectionKey]: !this.state[sectionKey]})
    }

    handleCancel = (e) => {
        e.preventDefault();

        this.cleanState();
        this.props.onCancel();
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    handleSubmit = (e) => {
        e.preventDefault();

        let data = {
            type: this.state.orgType,
            name: this.state.orgName,
            domain: this.state.orgDomain,
            address: {
                countryCode: this.state.orgCountry,
                city: this.state.orgCity,
                postal: this.state.orgPostal,
                street: this.state.orgStreet
            },
            contacts: {
                name: this.state.contactName,
                email: this.state.contactEmail,
                phone: this.state.contactPhone
            }
        }

        let bavaManaged = this.state.managedBy === AVAILABLE_MANAGED_OPTIONS.BAVA_MANAGED;

        this.props.onSubmit(data, bavaManaged);
    };

    cleanState = () => {
        this.setState({
            orgType: EDU_ORG,
            orgName: '',
            orgDomain: '',
            orgContinent: this.props.continentCode,
            orgCountry: '',
            orgCity: '',
            orgPostal: '',
            orgStreet: '',
            contactName: '',
            contactEmail: '',
            contactPhone: '',
            managedBy: AVAILABLE_MANAGED_OPTIONS.BAVA_MANAGED,
            addressSectionOpen: false,
            contactSectionOpen: false,
            fullyRegistered: null
        })
    }
}

ValidateDomainModal.propTypes = {
    action: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    continentCode: PropTypes.string,
    continents: PropTypes.array,
    countries: PropTypes.array,
    translation: PropTypes.object,
    success: PropTypes.bool,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    onContinentSet: PropTypes.func,
    onContinentSetByCountry: PropTypes.func,
    onClose: PropTypes.func
};

export default ValidateDomainModal;



// WEBPACK FOOTER //
// ./src/components/accreditationManagement/accreditationModal/validateDomainModal/ValidateDomainModal.js